import {
  createTransportClient,
  getRouteOrganizationSlug,
  getTransportTokenForOrganization,
  type TransportRequest,
} from "@/transport"

import { logger } from "@/lib/logger"
import type { LaunchRunV1Request } from "@/types/api/runs-api-v1"

interface RerunMetricsV1Request {
  simulation_ids: string[]
  metric_ids: string[]
  dev_id?: string
  workspace_id?: string | null
}

const log = logger("LaunchEvaluation")

interface BrowserClerkSession {
  getToken: (options?: {
    organizationId?: string
    skipCache?: boolean
  }) => Promise<string | null>
}

interface BrowserClerk {
  organization?: {
    id?: string | null
    slug?: string | null
  } | null
  session?: BrowserClerkSession | null
}

function getBrowserClerk(): BrowserClerk {
  if (typeof window === "undefined") {
    throw new Error("Launch evaluation is only available in the browser.")
  }

  const clerk = (window as typeof window & { Clerk?: BrowserClerk }).Clerk
  if (!clerk?.session?.getToken) {
    throw new Error("Launch evaluation requires an active session.")
  }

  return clerk
}

function getLaunchOrganizationContext(clerk: BrowserClerk) {
  const routeSlug = getRouteOrganizationSlug(window.location.pathname)
  const activeSlug = clerk.organization?.slug ?? null
  const slug = routeSlug ?? activeSlug
  const activeOrganizationMatchesTarget =
    slug !== null && activeSlug !== null && slug === activeSlug

  return {
    activeOrganizationId: activeOrganizationMatchesTarget
      ? (clerk.organization?.id ?? undefined)
      : undefined,
    slug,
  }
}

function createLaunchTransportClient(workspaceId?: string | null) {
  const clerk = getBrowserClerk()
  const organizationContext = getLaunchOrganizationContext(clerk)

  return createTransportClient({
    getOrganizationContext: () =>
      organizationContext.slug ? { slug: organizationContext.slug } : null,
    getWorkspaceId: () => workspaceId ?? null,
    getToken: () =>
      getTransportTokenForOrganization({
        getToken: clerk.session!.getToken,
        organizationId: organizationContext.activeOrganizationId,
        organizationSlug: organizationContext.slug ?? undefined,
      }),
  })
}

/**
 * Launch an evaluation using the Runs API v1 endpoint.
 * Used for standard model types (VOICE, CHAT, SMS, etc.).
 * Uses BFF pattern with internal API key authentication.
 */
export async function launchEvaluationV1(
  config: LaunchRunV1Request,
  workspaceId?: string | null,
) {
  try {
    const response = await createLaunchTransportClient(
      workspaceId,
    ).operations.launchRun(config as TransportRequest<"launchRun">)
    return {
      statusCode: 200,
      run_id: response.run.run_id,
      run: response.run,
    }
  } catch (error) {
    log.error("Error launching evaluation via v1 API", error)
    throw error
  }
}

/**
 * Re-score existing simulations against a metric set.
 *
 * Runs through session-authenticated transport, which derives the organization from the route
 * rather than taking it from the caller.
 *
 * Batched at 100, the contract's per-request ceiling on simulation ids. A batch that fails does not
 * stop the rest: a mixed result returns per-batch outcomes, and only an all-failed run throws.
 */
export async function rerunMetricsV1(config: RerunMetricsV1Request) {
  const { workspace_id: workspaceId, ...request } = config
  const client = createLaunchTransportClient(workspaceId)
  const responses: unknown[] = []
  let failedBatchCount = 0
  let successfulBatchCount = 0
  let firstError: unknown = null

  for (let index = 0; index < config.simulation_ids.length; index += 100) {
    try {
      const response = await client.operations.rerunMetrics({
        ...request,
        simulation_ids: config.simulation_ids.slice(index, index + 100),
      } as TransportRequest<"rerunMetrics">)
      successfulBatchCount += 1
      responses.push(response)
    } catch (error) {
      failedBatchCount += 1
      firstError ??= error
      responses.push(error)
    }
  }

  if (failedBatchCount > 0 && successfulBatchCount > 0) {
    return {
      batches: responses,
      failed_batch_count: failedBatchCount,
      successful_batch_count: successfulBatchCount,
    }
  }
  if (failedBatchCount > 0) {
    log.error("Error rerunning metrics via transport", firstError)
    throw firstError instanceof Error
      ? firstError
      : new Error("Rerun metrics failed.")
  }
  return responses.length === 1 ? responses[0] : responses
}
